import { domainError, type DomainError } from '../shared/domain-error';
import type { DisciplineId, DisciplineSteelRateId, ProjectId, UserId } from '../shared/ids';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { AppliesTo } from './applies-to';
import type { MeasureBy } from './geometry';
import type { IndexOrigin } from './index-origin';

/**
 * O aço da obra, por bitola (Fase 27, D11, D12 e F5).
 *
 * Três decisões moram aqui:
 *
 * - **o aço é comprado só no CORTE E DOBRA DE FERRAGEM** (RN-366), em qualquer disciplina. A
 *   MONTAGEM e a INSTALAÇÃO contam o mesmo kg, mas como quantidade do serviço, sem item de
 *   aço: a planilha que punha os 31,56 kg nas duas comprava o aço duas vezes;
 * - **o kg sai da taxa por bitola da disciplina** (RN-367): o volume da peça vezes os kg/m³
 *   de cada bitola. A taxa mora na disciplina, por obra, e não no item, para que os três
 *   serviços de ferragem leiam o mesmo número (ADR-176). A estaca, na etapa 1b, troca a
 *   taxa pelo tipo dela;
 * - **o item de aço reparte o medido pela participação da bitola** (RN-378). Com o medido
 *   corrigido ou digitado, a divisão entre bitolas continua sendo a da taxa.
 *
 * A bitola é uma lista fechada: é o que a siderúrgica vende, e o que o item da planilha
 * escreve de dez jeitos ("CA 50 - 16 MM", "CA-50 - 6.3 MM", "CA50 16mm").
 */

export type SteelGauge =
  | 'CA60_4_2'
  | 'CA60_5_0'
  | 'CA50_6_3'
  | 'CA50_8_0'
  | 'CA50_10_0'
  | 'CA50_12_5'
  | 'CA50_16_0'
  | 'CA50_20_0'
  | 'CA50_25_0'
  | 'CA50_32_0';

export interface SteelGaugeInfo {
  readonly code: SteelGauge;
  readonly steelClass: 'CA-50' | 'CA-60';
  /** Milímetros. */
  readonly diameterMm: number;
  /** "CA 50 - 16 MM" — o rótulo da tela e do modelo, no jeito em que a obra escreve. */
  readonly label: string;
  /**
   * A massa nominal, em kg por metro (NBR 7480). É o que converte o quilo do orçamento na
   * barra que o compras vende (Fase 29): a barra de 12 m de 16 mm pesa 18,94 kg.
   */
  readonly kgPerMeter: number;
}

const bitola = (
  code: SteelGauge,
  steelClass: 'CA-50' | 'CA-60',
  diameterMm: number,
  kgPerMeter: number,
): SteelGaugeInfo => ({
  code,
  steelClass,
  diameterMm,
  kgPerMeter,
  label: `${steelClass.replace('-', ' ')} - ${diameterMm.toLocaleString('pt-BR', {
    minimumFractionDigits: diameterMm % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })} MM`,
});

/** Em ordem de diâmetro — a ordem da tela e do modelo. */
export const STEEL_GAUGES: readonly SteelGaugeInfo[] = [
  bitola('CA60_4_2', 'CA-60', 4.2, 0.109),
  bitola('CA60_5_0', 'CA-60', 5, 0.154),
  bitola('CA50_6_3', 'CA-50', 6.3, 0.245),
  bitola('CA50_8_0', 'CA-50', 8, 0.395),
  bitola('CA50_10_0', 'CA-50', 10, 0.617),
  bitola('CA50_12_5', 'CA-50', 12.5, 0.963),
  bitola('CA50_16_0', 'CA-50', 16, 1.578),
  bitola('CA50_20_0', 'CA-50', 20, 2.466),
  bitola('CA50_25_0', 'CA-50', 25, 3.853),
  bitola('CA50_32_0', 'CA-50', 32, 6.313),
];

const POR_CODIGO: ReadonlyMap<string, SteelGaugeInfo> = new Map(
  STEEL_GAUGES.map((info) => [info.code, info]),
);

export function isSteelGauge(value: string): value is SteelGauge {
  return POR_CODIGO.has(value);
}

export function steelGaugeInfo(code: SteelGauge): SteelGaugeInfo {
  return POR_CODIGO.get(code)!;
}

export function steelGaugeLabel(code: SteelGauge): string {
  return steelGaugeInfo(code).label;
}

/**
 * A bitola em polegada, como o comércio a escreve ao lado do milímetro: "VERGALHAO CA50 5/8
 * 16MM". O 5/8 é o 16 mm — e, sem o milímetro escrito, é por ela que a bitola se acha.
 */
const POLEGADAS: Readonly<Record<string, number>> = {
  '3/16': 5,
  '1/4': 6.3,
  '5/16': 8,
  '3/8': 10,
  '1/2': 12.5,
  '5/8': 16,
  '3/4': 20,
};

/**
 * A bitola que o texto escreve — `undefined` quando ele não escreve uma.
 *
 * Exige a classe (CA 50, CA-60, CA50) **e** um diâmetro da lista. Sem a classe, "10 MM" pode
 * ser parafuso, broca ou eletroduto, e adivinhar aço ali poria item que não é aço no corte e
 * dobra. O diâmetro aceita vírgula e ponto (6,3 e 6.3), e o "Ø" e o "MM" são opcionais.
 *
 * O milímetro escrito manda: "CA50 5/8 16MM" é 16 mm, e não 5 — a fração de polegada antes
 * dele não é diâmetro, e lê-la como diâmetro deixava a barra sem bitola, e o aço da montagem
 * virava item de compra em dobro. Sem o milímetro, vale o primeiro número fora de fração; e,
 * sem número nenhum, a polegada ("CA50 5/8" é 16 mm).
 */
export function parseSteelGauge(text: string): SteelGauge | undefined {
  const limpo = normalizeName(text).replace(/ø/g, ' ');
  const classe = /\bca\s*-?\s*(50|60)\b/.exec(limpo);
  if (classe === null) return undefined;
  const depois = limpo.slice(classe.index + classe[0].length);
  const comMilimetro = /(\d+(?:[.,]\d+)?)\s*mm\b/.exec(depois);
  const solto = /(\d+(?:[.,]\d+)?)/.exec(depois.replace(/\d+\s*\/\s*\d+/g, ' '));
  const fracao = /(\d+)\s*\/\s*(\d+)/.exec(depois);
  const diametro =
    comMilimetro !== null
      ? Number(comMilimetro[1]!.replace(',', '.'))
      : solto !== null
        ? Number(solto[1]!.replace(',', '.'))
        : fracao !== null
          ? POLEGADAS[`${fracao[1]!}/${fracao[2]!}`]
          : undefined;
  if (diametro === undefined) return undefined;
  const steelClass = classe[1] === '50' ? 'CA-50' : 'CA-60';
  return STEEL_GAUGES.find(
    (info) => info.steelClass === steelClass && Math.abs(info.diameterMm - diametro) < 0.05,
  )?.code;
}

// ── O serviço que compra ────────────────────────────────────────────────────────

/** O único serviço que leva item de aço (RN-366). */
export const STEEL_PURCHASE_SERVICE = 'CORTE E DOBRA DE FERRAGEM';

export function isSteelPurchaseService(serviceName: string): boolean {
  return normalizeName(serviceName) === normalizeName(STEEL_PURCHASE_SERVICE);
}

/**
 * O serviço lê a taxa de aço da disciplina (Fase 33, RN-438): mede por ela (`ACO`), ou é o CORTE
 * E DOBRA, que reparte os kg pelas bitolas dela. Mudar a taxa muda a quantidade dele.
 */
export function readsSteelRate(service: {
  readonly name: string;
  readonly measureBy?: MeasureBy;
}): boolean {
  return service.measureBy === 'ACO' || isSteelPurchaseService(service.name);
}

// ── A taxa da disciplina ────────────────────────────────────────────────────────

/** Quantos kg da bitola vão por m³ de concreto da peça (RN-367). */
export interface SteelRate {
  readonly gauge: SteelGauge;
  readonly kgPerM3: number;
  /**
   * De onde veio a taxa (Fase 40, etapa 3 — RN-533): `MEDIA` é a que a média do aço da laje deu
   * quando a tipologia escolheu o serviço; `DIGITADO` a que alguém escreveu. Ausente é a de antes:
   * digitada.
   */
  readonly origin?: IndexOrigin;
  /** A foto da média do dia em que a taxa foi gravada (D8). */
  readonly average?: SteelRateAverage;
}

/** A foto da média da taxa de uma bitola (RN-532): o valor, o dia e de quantas obras. */
export interface SteelRateAverage {
  readonly rate: number;
  /** `aaaa-mm-dd`. */
  readonly at: string;
  readonly projects: number;
}

export function sameSteelRateAverage(a?: SteelRateAverage, b?: SteelRateAverage): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.rate === b.rate && a.at === b.at && a.projects === b.projects;
}

const DIA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A origem e a foto que a taxa pode guardar, já conferidas — a mesma regra do índice
 * (`indexProvenance`): a `MEDIA` sem foto vira `DIGITADO`, e a foto fora de forma é descartada.
 */
export function steelRateProvenance(
  origin: IndexOrigin | undefined,
  average: SteelRateAverage | undefined,
): { readonly origin?: IndexOrigin; readonly average?: SteelRateAverage } {
  const foto =
    average !== undefined &&
    Number.isFinite(average.rate) &&
    average.rate > 0 &&
    DIA.test(average.at) &&
    Number.isInteger(average.projects) &&
    average.projects >= 0
      ? { ...average, rate: Math.round(average.rate * 1000) / 1000 }
      : undefined;
  const origem: IndexOrigin | undefined =
    origin === 'MEDIA' && foto === undefined ? 'DIGITADO' : origin;
  return {
    ...(origem === undefined ? {} : { origin: origem }),
    ...(foto === undefined ? {} : { average: foto }),
  };
}

export const STEEL_RATE_ERROR = {
  INVALID_RATE: 'TAKEOFF.STEEL_RATE_INVALID',
  DUPLICATED_GAUGE: 'TAKEOFF.STEEL_RATE_DUPLICATED_GAUGE',
} as const;

/** Três casas, como a coluna (`numeric(12, 3)`): 45,125 kg/m³ é o máximo que alguém escreve. */
const CASAS_DA_TAXA = 3;

/**
 * A taxa conferida: positiva, uma linha por bitola, na ordem das bitolas.
 *
 * Zero não é taxa: a bitola que a disciplina não usa simplesmente não tem linha. Guardar o
 * zero faria o modelo exportado mostrar dez linhas de zero por disciplina, e a pergunta "esta
 * disciplina tem taxa?" passaria a depender de somar.
 */
export function normalizeSteelRates(
  rates: readonly {
    readonly gauge: SteelGauge;
    readonly kgPerM3: number;
    readonly origin?: IndexOrigin | undefined;
    readonly average?: SteelRateAverage | undefined;
  }[],
): Result<readonly SteelRate[], DomainError> {
  const vistas = new Set<SteelGauge>();
  for (const taxa of rates) {
    if (!Number.isFinite(taxa.kgPerM3) || taxa.kgPerM3 <= 0) {
      return err(
        domainError(
          STEEL_RATE_ERROR.INVALID_RATE,
          `A taxa de ${steelGaugeLabel(taxa.gauge)} deve ser maior que zero.`,
          { gauge: taxa.gauge, kgPerM3: taxa.kgPerM3 },
        ),
      );
    }
    if (vistas.has(taxa.gauge)) {
      return err(
        domainError(
          STEEL_RATE_ERROR.DUPLICATED_GAUGE,
          `A bitola ${steelGaugeLabel(taxa.gauge)} aparece duas vezes na mesma disciplina.`,
          { gauge: taxa.gauge },
        ),
      );
    }
    vistas.add(taxa.gauge);
  }
  const fator = 10 ** CASAS_DA_TAXA;
  return ok(
    STEEL_GAUGES.flatMap((info) => {
      const taxa = rates.find((candidata) => candidata.gauge === info.code);
      return taxa === undefined
        ? []
        : [
            {
              gauge: info.code,
              kgPerM3: Math.round(taxa.kgPerM3 * fator) / fator,
              ...steelRateProvenance(taxa.origin, taxa.average),
            },
          ];
    }),
  );
}

/** Os kg de aço por m³ da disciplina, todas as bitolas somadas. Sem taxa, zero. */
export function totalSteelRate(rates: readonly SteelRate[] | undefined): number {
  return (rates ?? []).reduce((soma, taxa) => soma + taxa.kgPerM3, 0);
}

/**
 * Quanto do aço é de cada bitola — a fração que reparte o medido entre os itens (RN-378).
 *
 * Vazio quando não há taxa: aí o item de aço não calcula, e a tela diz que falta a taxa.
 */
export function gaugeShares(
  rates: readonly SteelRate[] | undefined,
): Readonly<Partial<Record<SteelGauge, number>>> {
  const total = totalSteelRate(rates);
  if (total <= 0) return {};
  const fracoes: Partial<Record<SteelGauge, number>> = {};
  for (const taxa of rates ?? []) fracoes[taxa.gauge] = taxa.kgPerM3 / total;
  return fracoes;
}

// ── A taxa por elemento ─────────────────────────────────────────────────────────

/**
 * A lista de um elemento numa disciplina que mistura elementos (Fase 42, etapa 3 — G4, RN-573,
 * ADR-189): na PILARES DE CONTENÇÃO, a do muro e a do pilar de contenção.
 */
export interface ElementSteelRates {
  readonly element: AppliesTo;
  readonly rates: readonly SteelRate[];
}

/**
 * A taxa que um serviço lê na disciplina dele (RN-573): a lista do elemento que ele mede, quando
 * a disciplina tem uma; sem ela, a da disciplina inteira. A lista vale inteira — a do elemento não
 * se completa com bitolas da disciplina, como a da peça (Fase 41).
 */
export function steelRatesFor(
  disciplina: {
    readonly steelRates?: readonly SteelRate[];
    readonly elementSteelRates?: readonly ElementSteelRates[];
  },
  element: AppliesTo | undefined,
): readonly SteelRate[] | undefined {
  const doElemento =
    element === undefined
      ? undefined
      : disciplina.elementSteelRates?.find((lista) => lista.element === element)?.rates;
  return doElemento !== undefined && doElemento.length > 0 ? doElemento : disciplina.steelRates;
}

/**
 * A mesma escolha sobre as linhas gravadas de **uma** disciplina (`DisciplineSteelRate`): as do
 * elemento, quando há; sem elas, as da disciplina inteira (as sem elemento). É o que o servidor
 * usa onde lê a tabela, e não o catálogo montado.
 */
export function steelRowsFor<T extends { readonly element?: AppliesTo }>(
  rows: readonly T[],
  element: AppliesTo | undefined,
): readonly T[] {
  const doElemento = element === undefined ? [] : rows.filter((linha) => linha.element === element);
  return doElemento.length > 0 ? doElemento : rows.filter((linha) => linha.element === undefined);
}

/**
 * Os elementos que os serviços de aço de uma disciplina medem, na ordem em que aparecem. Mais de
 * um é a disciplina que mistura (RN-573): a tela mostra uma lista por elemento, e a média vai para
 * a de cada um.
 */
export function steelElementsOf(
  services: readonly {
    readonly name: string;
    readonly measureBy?: MeasureBy;
    readonly appliesTo?: AppliesTo;
  }[],
): readonly AppliesTo[] {
  const elementos: AppliesTo[] = [];
  for (const servico of services) {
    if (!readsSteelRate(servico)) continue;
    const elemento = servico.appliesTo;
    if (elemento === undefined || elemento === 'NENHUM' || elementos.includes(elemento)) continue;
    elementos.push(elemento);
  }
  return elementos;
}

/**
 * O serviço com a taxa de aço da disciplina dele — o que a medida `ACO` multiplica (ADR-176).
 *
 * A taxa é da disciplina, e não do serviço: o CORTE E DOBRA, a MONTAGEM e a INSTALAÇÃO leem
 * o mesmo número, e é por isso que quem monta a conta (a tela, o orçamento) pendura a soma
 * das bitolas no serviço antes de perguntar ao desenho. Sem taxa, o serviço volta como veio,
 * e a medida `ACO` não mede — a tela diz "sem taxa de aço".
 */
export function withSteelRate<S extends object>(
  service: S,
  rates: readonly SteelRate[] | undefined,
): S & { readonly steelRatePerM3?: number } {
  const taxa = totalSteelRate(rates);
  return taxa > 0 ? { ...service, steelRatePerM3: taxa } : service;
}

// ── A taxa gravada ──────────────────────────────────────────────────────────────

/**
 * A taxa de uma bitola numa disciplina, nesta obra — a linha de `discipline_steel_rates`
 * (`0053`).
 *
 * Da obra mesmo quando a disciplina é global (RN-254, como o item): duas obras sobre o mesmo
 * PILARES global armam com taxas diferentes.
 */
export interface DisciplineSteelRate {
  readonly id: DisciplineSteelRateId;
  readonly projectId: ProjectId;
  readonly disciplineId: DisciplineId;
  readonly gauge: SteelGauge;
  readonly kgPerM3: number;
  /**
   * O elemento da lista (Fase 42, `0098` — RN-573): ausente é a disciplina inteira; com
   * elemento, a lista vale só para os serviços que o medem.
   */
  readonly element?: AppliesTo;
  /** A origem e a foto da taxa (Fase 40, `0093`). */
  readonly origin?: IndexOrigin;
  readonly average?: SteelRateAverage;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly updatedByUserId?: UserId;
  readonly deletedAt?: string;
}

export interface SteelRatesPlan {
  readonly created: readonly DisciplineSteelRate[];
  /** Só as que mudaram de número, de origem ou de foto — reenviar a mesma taxa não regrava nada. */
  readonly updated: readonly DisciplineSteelRate[];
  /** As bitolas que não vieram mais, já com `deletedAt`. */
  readonly removed: readonly DisciplineSteelRate[];
}

/**
 * As taxas de uma disciplina trocadas pelas que a tela mandou — **substituição**, como os
 * itens (RN-255): a bitola que não veio é a que alguém tirou.
 *
 * Casa pela bitola, que é a chave da linha: a mesma bitola mantém o id e só muda o número; a
 * que sumiu sai por exclusão lógica (o índice único da `0053` é parcial), e a nova nasce.
 * `incoming` já passou por `normalizeSteelRates` — positiva, sem bitola repetida.
 */
export function planSteelRates(input: {
  readonly projectId: ProjectId;
  readonly disciplineId: DisciplineId;
  /**
   * A lista de que elemento (RN-573): ausente, a da disciplina inteira. Só as gravadas do mesmo
   * elemento entram na troca — a lista do muro não apaga a do pilar.
   */
  readonly element?: AppliesTo;
  readonly existing: readonly DisciplineSteelRate[];
  readonly incoming: readonly SteelRate[];
  readonly now: string;
  readonly userId?: UserId;
  readonly newId: () => DisciplineSteelRateId;
}): SteelRatesPlan {
  const doElemento = input.existing.filter((taxa) => taxa.element === input.element);
  const gravadas = new Map(doElemento.map((taxa) => [taxa.gauge, taxa]));
  const quem = input.userId === undefined ? {} : { updatedByUserId: input.userId };
  const created: DisciplineSteelRate[] = [];
  const updated: DisciplineSteelRate[] = [];

  for (const taxa of input.incoming) {
    const anterior = gravadas.get(taxa.gauge);
    if (anterior === undefined) {
      created.push({
        id: input.newId(),
        projectId: input.projectId,
        disciplineId: input.disciplineId,
        ...(input.element === undefined ? {} : { element: input.element }),
        gauge: taxa.gauge,
        kgPerM3: taxa.kgPerM3,
        ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
        ...(taxa.average === undefined ? {} : { average: taxa.average }),
        createdAt: input.now,
        updatedAt: input.now,
        ...quem,
      });
    } else if (
      anterior.kgPerM3 !== taxa.kgPerM3 ||
      anterior.origin !== taxa.origin ||
      !sameSteelRateAverage(anterior.average, taxa.average)
    ) {
      const { origin: _origem, average: _foto, ...semAOrigem } = anterior;
      updated.push({
        ...semAOrigem,
        kgPerM3: taxa.kgPerM3,
        ...(taxa.origin === undefined ? {} : { origin: taxa.origin }),
        ...(taxa.average === undefined ? {} : { average: taxa.average }),
        updatedAt: input.now,
        ...quem,
      });
    }
  }

  const vieram = new Set(input.incoming.map((taxa) => taxa.gauge));
  const removed = doElemento
    .filter((taxa) => !vieram.has(taxa.gauge))
    .map((taxa): DisciplineSteelRate => ({
      ...taxa,
      updatedAt: input.now,
      deletedAt: input.now,
      ...quem,
    }));

  return { created, updated, removed };
}
